import { computed, ref } from 'vue';
import { useDirectorySearchStore } from '@dashboardApp/stores/DirectorySearchStore';
import { DEFAULT_SCORE_COLOR_CLASS, getOpportunityColorClass, humanizeScoreText } from '@dashboardApp/helpers/insightScores';

export default function useInsights () {
	const { selectQueryAndPreSearch } = useDirectorySearchStore();

	const trend = ref(null);
	const humanTrend = ref(null);
	const scores = ref(null);
	const sourcingInProgress = ref(false);

	/**
	 * Copied logic from MI
	 */
	const trendThreshold = computed(() => {
	    if (trend.value) {
	        if (trend.value > 0.5) {
	            return 2;
	        } else if (trend.value > 0) {
	            return 1;
	        } else if (trend.value < -0.5) {
	            return -2;
	        } else if (trend.value < 0) {
	            return -1;
	        }
	        return 0;
	    }
	});

	const growthClass = computed(() => {
		switch (trendThreshold.value) {
	        case 2:
	        case 1:
	            return 'text-success';
	        case 0:
	            return 'text-info';
	        case -1:
	        case -2:
	            return 'text-danger';
	    }
	});

	const growthText = computed(() => {
		if (humanTrend.value === null) {
			return;
		}

		const sign = humanTrend.value > 0 ? '+' : '';
		return `${sign}${humanTrend.value}%`;
	});

	const sourceProduct = (term) => {
		if (sourcingInProgress.value) {
			return;
		}
		sourcingInProgress.value = true;
	    selectQueryAndPreSearch(term, null, 'products').then(() => sourcingInProgress.value = false);
	};

	const sourceSupplier = (term) => {
		if (sourcingInProgress.value) {
			return;
		}
		sourcingInProgress.value = true;
	    selectQueryAndPreSearch(term, null, 'suppliers').then(() => sourcingInProgress.value = false);
	};

	const buildScoreDetail = (key, { textKey, suffix, className, classResolver } = {}) => {
		if (!scores.value) {
			return null;
		}

		const rawValue = scores.value[key];
		if (rawValue === null || rawValue === undefined) {
			return null;
		}

		const numericValue = Number(rawValue);
		if (Number.isNaN(numericValue) || numericValue < 0) {
			return null;
		}

		const textValue = scores.value[textKey || `${key}_text`];
		const label = humanizeScoreText(textValue);
		if (!label) {
			return null;
		}

		const colorClass = typeof classResolver === 'function' ? classResolver(textValue) : (className || DEFAULT_SCORE_COLOR_CLASS);
		const valueRounded = Math.round(numericValue);

		return {
			label,
			labelWithSuffix: suffix ? `${label} ${suffix}` : label,
			value: valueRounded,
			valueLabel: `${valueRounded}%`,
			colorClass,
		};
	};

	const scoreDetails = computed(() => ({
		opportunity: buildScoreDetail('opportunity', { textKey: 'opportunity_text', classResolver: getOpportunityColorClass }),
		demand: buildScoreDetail('demand', { textKey: 'demand_text', suffix: 'Demand', className: DEFAULT_SCORE_COLOR_CLASS }),
		competition: buildScoreDetail('competition', { textKey: 'competition_text', suffix: 'Competition', className: DEFAULT_SCORE_COLOR_CLASS }),
	}));

	const scoreMetrics = computed(() => {
		const metrics = [];
		const details = scoreDetails.value;

		['opportunity', 'demand', 'competition'].forEach((key) => {
			const detail = details[key];
			if (detail) {
				metrics.push({ text: detail.labelWithSuffix, colorClass: detail.colorClass });
			}
		});

		return metrics;
	});

    return {
        trend, humanTrend, trendThreshold, growthClass, growthText,
		scores, scoreMetrics, scoreDetails,
        sourcingInProgress, sourceProduct, sourceSupplier
    }
}
